import { useCallback, useMemo } from 'react'
import { useMemberPortalApi } from './memberPortalApiClient'

function unwrapResponse(payload, fallbackError) {
  const data = payload?.data ?? payload
  if (data?.ok === false) {
    throw new Error(data?.error || fallbackError)
  }
  return data?.data ?? data
}

export function useMemberActivitiesApi() {
  const { memberPortalApiGet, memberPortalApiPost, memberPortalApiFetch } = useMemberPortalApi()

  const listActivities = useCallback(async (clubCode) => {
    const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/activities`)
    const json = await res.json()
    return unwrapResponse(json, 'member_activities_list_failed')
  }, [memberPortalApiGet])

  const getActivity = useCallback(async (clubCode, activityId) => {
    const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/activities/${activityId}`)
    const json = await res.json()
    return unwrapResponse(json, 'member_activity_get_failed')
  }, [memberPortalApiGet])

  const listActivitySessions = useCallback(async (clubCode, activityId) => {
    const res = await memberPortalApiGet(
      `/api/members-portal/${clubCode}/activities/${activityId}/sessions`
    )
    const json = await res.json()
    return unwrapResponse(json, 'member_activity_sessions_failed')
  }, [memberPortalApiGet])

  const createBooking = useCallback(async (clubCode, activityId, sessionId) => {
    const res = await memberPortalApiPost(
      `/api/members-portal/${clubCode}/activities/${activityId}/sessions/${sessionId}/bookings`
    )
    const json = await res.json()
    return unwrapResponse(json, 'activity_booking_failed')
  }, [memberPortalApiPost])

  const cancelBooking = useCallback(async (clubCode, activityId, bookingId) => {
    const res = await memberPortalApiFetch(
      `/api/members-portal/${clubCode}/activities/${activityId}/bookings/${bookingId}`,
      { method: 'DELETE' }
    )
    const json = await res.json()
    return unwrapResponse(json, 'activity_booking_cancel_failed')
  }, [memberPortalApiFetch])

  const listManagedActivities = useCallback(async (clubCode) => {
    const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/me/managed-activities`)
    const json = await res.json()
    return unwrapResponse(json, 'managed_activities_list_failed')
  }, [memberPortalApiGet])

  const listManageSessions = useCallback(async (clubCode, activityId) => {
    const res = await memberPortalApiGet(
      `/api/members-portal/${clubCode}/manage/activities/${activityId}/sessions`
    )
    const json = await res.json()
    return unwrapResponse(json, 'manage_activity_sessions_failed')
  }, [memberPortalApiGet])

  const createManageSession = useCallback(async (clubCode, activityId, payload) => {
    const res = await memberPortalApiPost(
      `/api/members-portal/${clubCode}/manage/activities/${activityId}/sessions`,
      payload
    )
    const json = await res.json()
    return unwrapResponse(json, 'manage_activity_session_create_failed')
  }, [memberPortalApiPost])

  const updateManageSession = useCallback(async (clubCode, activityId, sessionId, payload) => {
    const res = await memberPortalApiFetch(
      `/api/members-portal/${clubCode}/manage/activities/${activityId}/sessions/${sessionId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )
    const json = await res.json()
    return unwrapResponse(json, 'manage_activity_session_update_failed')
  }, [memberPortalApiFetch])

  const listManageRoster = useCallback(async (clubCode, activityId, sessionId) => {
    const res = await memberPortalApiGet(
      `/api/members-portal/${clubCode}/manage/activities/${activityId}/sessions/${sessionId}/roster`
    )
    const json = await res.json()
    return unwrapResponse(json, 'manage_activity_roster_failed')
  }, [memberPortalApiGet])

  return useMemo(
    () => ({
      listActivities,
      getActivity,
      listActivitySessions,
      createBooking,
      cancelBooking,
      listManagedActivities,
      listManageSessions,
      createManageSession,
      updateManageSession,
      listManageRoster,
    }),
    [
      listActivities,
      getActivity,
      listActivitySessions,
      createBooking,
      cancelBooking,
      listManagedActivities,
      listManageSessions,
      createManageSession,
      updateManageSession,
      listManageRoster,
    ]
  )
}
